import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'

import Routes from 'commons/js/routes/routes'
import { geocodePlace, geocodePoiParking, geocodeAirportParking, reverseSearch } from 'commons/js/services/geocoderService'
import { searchParkings, getParkingUrl } from 'parking-search-map/js/services/parkingsService'
import { makeParkingsFilter, getDefaultFilters, updateFiltersWithRestrictions } from 'parking-search-map/js/util/parkingsFilter'
import { compactObject } from 'commons/js/util/objectUtils'
import UrlParams from 'commons/js/util/UrlTo/UrlParams'

import { PARKING_ORDER_BY_DISTANCE } from 'commons/js/constants'

import {
    getPlace,
    getStart,
    getEnd,
} from './searchLenses'
import buildSearchParams from './buildSearchParams'
import buildGeocodeParams from './buildGeocodeParams'
import buildNavigateToParams from './buildNavigateToParams'
import isSearchEqual from './isSearchEqual'
import shouldUpdateUrl from 'commons/js/util/shouldUpdateUrl'
import mapSearchParams from './mapSearchParams'
import computeDistanceBetween from './computeDistanceBetween'
import { makeParkingsComparator } from '../../util/parkingComparator'

function getSearch ({ placeId, airportId, poiId, startDate, endDate }) {
    return compactObject({
        place_id: airportId ? null : placeId,
        airport_id: airportId,
        poi_id: poiId,
        start: startDate,
        end: endDate,
    })
}

function hasAirportSearchChanged (prevProps, props) {
    return prevProps.airportId !== props.airportId && props.airportId === undefined
}

class ParkingContainer extends React.Component {
    static propTypes = {
        children: PropTypes.func.isRequired,
        geocodePlace: PropTypes.func,
        geocodePoiParking: PropTypes.func,
        geocodeAirportParking: PropTypes.func,
        reverseSearch: PropTypes.func,
        searchParkings: PropTypes.func,
        computeDistanceBetween: PropTypes.func,
        getParkingUrl: PropTypes.func,
        placeId: PropTypes.string,
        poiId: PropTypes.string,
        airportId: PropTypes.string,
        startDate: PropTypes.number.isRequired,
        endDate: PropTypes.number.isRequired,
        initialFilters: PropTypes.object,
        intl: PropTypes.object,
        // From connectUrl
        push: PropTypes.func,
        showFilterBox: PropTypes.bool.isRequired,
        showMowizTruckBox: PropTypes.bool.isRequired,
    }

    static defaultProps = {
        geocodePlace: geocodePlace,
        geocodePoiParking: geocodePoiParking,
        geocodeAirportParking: geocodeAirportParking,
        reverseSearch: reverseSearch,
        searchParkings: searchParkings,
        computeDistanceBetween: computeDistanceBetween,
        getParkingUrl: getParkingUrl,
        push: () => {},
    }

    state = {
        search: {
            place_id: this.props.placeId,
            airport_id: this.props.airportId,
            poi_id: this.props.poiId,
            start: this.props.startDate,
            end: this.props.endDate,
        },
        searchPoint: null,
        selectedParkingId: null,
        markedParkingId: null,
        parkings: [],
        isLoading: false,
        autoCenter: true,
        error: null,
        showFilters: false,
        filters: updateFiltersWithRestrictions(getDefaultFilters(), this.props.initialFilters),
        order: PARKING_ORDER_BY_DISTANCE,
    }

    resetSelectedParkings = this.resetSelectedParkings.bind(this)
    selectParking = this.selectParking.bind(this)
    markParking = this.markParking.bind(this)
    disableAutoCenter = this.disableAutoCenter.bind(this)
    updateUrl = this.updateUrl.bind(this)
    performSearch = this.performSearch.bind(this)
    computeDistanceTo = this.computeDistanceTo.bind(this)
    buildParkingUrl = this.buildParkingUrl.bind(this)
    receiveParkings = this.receiveParkings.bind(this)
    setSearch = this.setSearch.bind(this)
    handleFormSearch = this.handleFormSearch.bind(this)
    handleMapSearch = this.handleMapSearch.bind(this)
    setFilters = this.setFilters.bind(this)
    performGeolocation = this.performGeolocation.bind(this)
    setParkings = this.setParkings.bind(this)
    setLoading = this.setLoading.bind(this)
    setError = this.setError.bind(this)
    orderBy = this.orderBy.bind(this)
    getGeocodeMethod = this.getGeocodeMethod.bind(this)
    buildSearchParms = this.buildSearchParms.bind(this)

    componentDidMount () {
        this.performGeolocation(getSearch(this.props))
    }

    componentDidUpdate (prevProps, prevState) {
        const airportIdHasChanged = hasAirportSearchChanged(prevProps, this.props)
        const searchHasChanged = !isSearchEqual(prevState.search, this.state.search)
        const filtersHasChanged = prevState.filters !== this.state.filters
        const autoCenterHasChanged = prevState.autoCenter !== this.state.autoCenter

        if (airportIdHasChanged) {
            this.setFilters({
                parkingType: {
                    airport: false,
                },
            })
        }

        if (searchHasChanged || filtersHasChanged) {
            this.updateUrl(this.state)
        }

        if (searchHasChanged) {
            this.performSearch(this.state.search)
        }

        if (autoCenterHasChanged && prevState.autoCenter) {
            this.disableAutoCenter()
            return
        }

        const { airportId, placeId, poiId } = prevProps
        const locationHasChanged = (airportId !== this.props.airportId) || (placeId !== this.props.placeId) || (poiId !== this.props.poiId)
        if (locationHasChanged) {
            this.performGeolocation(getSearch(this.props))
        }
    }

    updateUrl ({ search, filters }) {
        const locationParams = new UrlParams()
        locationParams.loadFromUrl()
        const searchParams = new UrlParams()
        searchParams.loadFromUrl()
        searchParams.update(buildSearchParams({ search, filters }))

        if (shouldUpdateUrl(locationParams, searchParams)) {
            this.props.push(Routes.parkingsSearchPath(
                searchParams.getAll()
            ))
        }
    }

    performSearch (search) {
        this.setLoading(true)
        this.props.searchParkings(mapSearchParams(search))
            .then(this.receiveParkings)
    }

    getGeocodeMethod (isAirport, isPoi) {
        if (isAirport) {
            return this.props.geocodeAirportParking
        }
        if (isPoi) {
            return this.props.geocodePoiParking
        }
        return this.props.geocodePlace
    }

    buildSearchParms (search) {
        if (search.airport_id) {
            return parseInt(search.airport_id)
        }
        if (search.poi_id) {
            return parseInt(search.poi_id)
        }
        return buildGeocodeParams(search)
    }

    performGeolocation (search) {
        const geocode = this.getGeocodeMethod(!!search.airport_id, !!search.poi_id)
        const searchParams = this.buildSearchParms(search)
        // Grab place information (latitude, longitude...)
        geocode(searchParams, { formatMessage: this.props.intl.formatMessage })
            .then((place) => {
                ReactDOM.unstable_batchedUpdates(() => {
                    this.setSearchPoint(place)
                    this.setSearch({
                        start: getStart(search),
                        end: getEnd(search),
                        place,
                    })
                })
            }, (error) => {
                this.setError(error)
            })
    }

    handleFormSearch (formSearch) {
        // TODO: Extract to a function
        const search = {
            start: formSearch.startDate,
            end: formSearch.endDate,
            place: formSearch.place,
        }

        const aggregatedId = getPlace(search).aggregatedId
        const shouldPerformReverseSearch = !!aggregatedId

        if (!shouldPerformReverseSearch) {
            this.setSearch(search)
            return
        }

        this.props.reverseSearch(aggregatedId, { formatMessage: this.props.intl.formatMessage })
            .then(({ payload: place }) => {
                ReactDOM.unstable_batchedUpdates(() => {
                    this.enableAutoCenter()
                    this.setSearchPoint(place)
                    this.setSearch({
                        ...search,
                        airport_id: place.airport_id || null,
                        place_id: place.place_id || null,
                        place: {
                            ...search.place,
                            bounds: null,
                            airport_id: place.airport_id || null,
                            place_id: place.place_id,
                            poi_id: place.poi_id || null,
                            latitude: place.latitude,
                            longitude: place.longitude,
                        },
                    })
                })
            }, (error) => {
                this.setError(error)
            })
    }

    handleMapSearch (mapSearch) {
        ReactDOM.unstable_batchedUpdates(() => {
            this.disableAutoCenter()
            this.setSearch({
                place: {
                    ...this.state.search.place,
                    latitude: mapSearch.latitude,
                    longitude: mapSearch.longitude,
                    bounds: mapSearch.bounds,
                },
            })
        })
    }

    computeDistanceTo (latitude, longitude) {
        const searchPoint = this.state.searchPoint || {}
        return this.props.computeDistanceBetween(
            {
                latitude: searchPoint.latitude,
                longitude: searchPoint.longitude,
            },
            {
                latitude,
                longitude,
            }
        )
    }

    buildParkingUrl (parking) {
        return this.props.getParkingUrl(buildNavigateToParams(parking, this.state.search))
    }

    receiveParkings ({ canceled, error, result }) {
        if (canceled) {
            return
        }

        ReactDOM.unstable_batchedUpdates(() => {
            this.setLoading(false)

            if (error) {
                this.setError(error)
                return
            }

            this.setError(null)
            this.resetSelectedParkings()
            this.setParkings(result)
        })
    }

    resetSelectedParkings () {
        this.selectParking(null)
    }

    selectParking (selectedParkingId) {
        this.setState(() => ({selectedParkingId, markedParkingId: null}))
    }

    markParking (markedParkingId) {
        this.setState(() => ({markedParkingId, autoCenter: false}))
    }

    setSearch (search) {
        this.setState((prevState) => ({
            search: {
                ...prevState.search,
                ...search,
            },
        }))
    }

    setSearchPoint ({latitude, longitude}) {
        this.setState(() => ({searchPoint: {latitude, longitude}}))
    }

    setFilters (filters) {
        this.setState((prevState) => ({filters: updateFiltersWithRestrictions(prevState.filters, filters)}))
    }

    orderBy (order) {
        this.setState({order})
    }

    disableAutoCenter () {
        this.setState(() => ({autoCenter: false}))
    }

    enableAutoCenter () {
        this.setState(() => ({autoCenter: true}))
    }

    setParkings (parkings) {
        this.setState(() => ({parkings}))
    }

    setLoading (isLoading) {
        this.setState(() => ({isLoading}))
    }

    setError (error) {
        this.setState(() => ({error}))
    }

    onToggleFilters () {
        this.setState(({ showFilters }) => ({ showFilters: !showFilters }))
    }

    onToggleFilters = this.onToggleFilters.bind(this)

    onHideFilters () {
        this.setState({ showFilters: false })
    }

    onHideFilters = this.onHideFilters.bind(this)

    render () {
        const {
            search,
            parkings,
            selectedParkingId,
            markedParkingId,
            isLoading,
            autoCenter,
            error,
            searchPoint,
            filters,
            showFilters,
            order,
        } = this.state

        const filteredParkings = parkings.filter(makeParkingsFilter(filters)).sort(makeParkingsComparator(order, this.computeDistanceTo))

        return this.props.children({
            setFilters: this.setFilters,
            search,
            parkings: filteredParkings,
            selectedParkingId,
            markedParkingId,
            isLoading,
            autoCenter,
            error,
            searchPoint,
            filters,
            setFormSearch: this.handleFormSearch,
            setMapSearch: this.handleMapSearch,
            selectParking: this.selectParking,
            markParking: this.markParking,
            computeDistanceTo: this.computeDistanceTo,
            buildParkingUrl: this.buildParkingUrl,
            disableAutoCenter: this.disableAutoCenter,
            onHideFilters: this.onHideFilters,
            onToggleFilters: this.onToggleFilters,
            showFilters,
            onOrderBy: this.orderBy,
            order,
            showEventsBanner: !!getSearch(this.props).poi_id,
            showFilterBox: this.props.showFilterBox,
            showMowizTruckBox: this.props.showMowizTruckBox,
        })
    }
}

export default injectIntl(ParkingContainer)
